import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './lib/toast';
import { useSEO } from './lib/useSEO';
import { TenantProvider } from './context/TenantContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { affiliateApi } from './lib/api';
import { supabase } from './lib/supabase';
import { setDebugMode } from './lib/debugMode';
import { captureReferralCode } from './lib/referral';
import { FeatureKey, requiredTierLabel } from '../supabase/functions/_shared/planFeatures';
import Login from './pages/Login';
import Showcase from './pages/Showcase';
import LegalHub from './pages/LegalHub';
import SplashEngine from './pages/SplashEngine';
import SplashAutomation from './pages/SplashAutomation';
import SplashAffiliate from './pages/SplashAffiliate';
import SplashAIMarketing from './pages/SplashAIMarketing';

function PublicSEO() {
  const location = useLocation();
  useSEO(location.pathname);
  return null;
}

function ReferralTracker() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref && ref.startsWith('AME-')) {
      affiliateApi.trackClick(ref).catch(() => {});
      captureReferralCode(ref);
    }
  }, [location.search]);
  return null;
}

function OAuthRedirect({ isAuthenticated }: { isAuthenticated: boolean }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasOAuth = params.has('code') || params.has('error') || params.has('status') || params.has('connection_id') || (!isAuthenticated && location.pathname === '/scheduler');
  const status = params.get('status');
  const error = params.get('error');

  useEffect(() => {
    if (!hasOAuth) return;
    try { new BroadcastChannel('social_oauth').postMessage({ type: 'oauth_complete' }); } catch {}
  }, [hasOAuth]);

  if (!hasOAuth || isAuthenticated) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950 p-6 text-center">
      <div className="max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${error ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
          {error ? '!' : '✓'}
        </div>
        <h1 className="text-xl font-bold text-slate-800">{error ? 'Connection not completed' : 'Social account connected'}</h1>
        <p className="mt-2 text-sm leading-6 text-slate-500">
          {error ? `The social platform returned: ${params.get('message') || error}` : status === 'selection_required' ? 'Choose which Page to connect below.' : 'Return to the original AME tab. Your connected accounts are syncing there now.'}
        </p>
        <button onClick={() => window.close()} className="mt-6 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700">
          Close this tab
        </button>
      </div>
    </div>
  );
}

function LeadPageRedirect() {
  const { pageKey } = useParams();
  return <Navigate to={`/l/${pageKey ?? ''}`} replace />;
}

function ShellUnlessLeadPage({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  if (pathname.startsWith('/l/')) return <>{children}</>;
  return <Layout>{children}</Layout>;
}

function lazyWithRetry<T extends { default: React.ComponentType<any> }>(importFn: () => Promise<T>): React.LazyExoticComponent<T['default']> {
  return lazy(() =>
    importFn()
      .then(module => {
        sessionStorage.removeItem('ame_chunk_retry');
        return module;
      })
      .catch((err: Error) => {
        const isChunkError = err.message?.includes('Failed to fetch dynamically imported module') || err.message?.includes('Loading chunk');
        const retryKey = 'ame_chunk_retry';
        const hasRetried = sessionStorage.getItem(retryKey) === '1';

        if (isChunkError && !hasRetried) {
          sessionStorage.setItem(retryKey, '1');
          const url = new URL(window.location.href);
          url.searchParams.set('chunk_retry', Date.now().toString());
          window.location.replace(url.toString());
          return new Promise<T>(() => {});
        }

        sessionStorage.removeItem(retryKey);
        throw err;
      })
  );
}

const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const Setup = lazyWithRetry(() => import('./pages/Setup'));
const Research = lazyWithRetry(() => import('./pages/Research'));
const ContentGeneration = lazyWithRetry(() => import('./pages/ContentGeneration'));
const ContentLibrary = lazyWithRetry(() => import('./pages/ContentLibrary'));
const Automation = lazyWithRetry(() => import('./pages/Automation'));
const Analytics = lazyWithRetry(() => import('./pages/Analytics'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const PromptCreator = lazyWithRetry(() => import('./pages/PromptCreator'));
const PublishingHub = lazyWithRetry(() => import('./pages/PublishingHub'));
const ImageGenerator = lazyWithRetry(() => import('./pages/ImageGenerator'));
const RSSCreator = lazyWithRetry(() => import('./pages/RSSCreator'));
const EmailMarketing = lazyWithRetry(() => import('./pages/EmailMarketing'));
const LiveChatDemo = lazyWithRetry(() => import('./pages/LiveChatDemo'));
const LiveSupportPage = lazyWithRetry(() => import('./pages/LiveSupport'));
const AffiliateTracker = lazyWithRetry(() => import('./pages/AffiliateTracker'));
const SocialScheduler = lazyWithRetry(() => import('./pages/SocialScheduler'));
const CoverPageGen = lazyWithRetry(() => import('./components/CoverPageGen'));
const Pricing = lazyWithRetry(() => import('./pages/Pricing'));
import FeedbackWidget from './components/FeedbackWidget';
const MediaStudio = lazyWithRetry(() => import('./pages/MediaStudio'));
const TokenManagement = lazyWithRetry(() => import('./pages/TokenManagement'));
const Roadmap = lazyWithRetry(() => import('./pages/Roadmap'));
const SlideshowBuilder = lazyWithRetry(() => import('./pages/SlideshowBuilder'));
const LeadCaptureBuilder = lazyWithRetry(() => import('./pages/LeadCaptureBuilder'));
const PublicSlideshow = lazyWithRetry(() => import('./pages/PublicSlideshow'));
const PublicLeadPage = lazyWithRetry(() => import('./pages/PublicLeadPage'));
const Unsubscribed = lazyWithRetry(() => import('./pages/Unsubscribed'));
const Campaigns = lazyWithRetry(() => import('./pages/Campaigns'));
const AdminInbox = lazyWithRetry(() => import('./pages/AdminInbox'));
const TesterQuestionnaire = lazyWithRetry(() => import('./pages/TesterQuestionnaire'));
const ContentPermission = lazyWithRetry(() => import('./pages/ContentPermission'));
const TesterUploads = lazyWithRetry(() => import('./pages/TesterUploads'));
const Testimonials = lazyWithRetry(() => import('./pages/Testimonials'));
const TestimonialAdmin = lazyWithRetry(() => import('./pages/TestimonialAdmin'));
const MembersAdmin = lazyWithRetry(() => import('./pages/MembersAdmin'));
const MarketingManager = lazyWithRetry(() => import('./pages/MarketingManager'));
const LeadGeneration = lazyWithRetry(() => import('./pages/LeadGeneration'));
const OutputShowcase = lazyWithRetry(() => import('./pages/OutputShowcase'));
const BlogRotation = lazyWithRetry(() => import('./pages/BlogRotation'));
const BlogLibrary = lazyWithRetry(() => import('./pages/BlogLibrary'));
const PublicBlog = lazyWithRetry(() => import('./pages/PublicBlog'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="flex items-center gap-3 text-slate-500 font-mono text-sm">
        <div className="w-5 h-5 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
        Loading...
      </div>
    </div>
  );
}

function FeatureRoute({ feature, children }: { feature: FeatureKey; children: React.ReactNode }) {
  const { accessReady, canUse } = useAuth();
  if (!accessReady) return <PageLoader />;
  if (canUse(feature)) return <>{children}</>;
  return <Navigate to={requiredTierLabel(feature) ? `/pricing?locked=${feature}` : '/dashboard'} replace />;
}

function DebugModeLoader() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) { setDebugMode(false); return; }
    supabase.from('user_preferences').select('debug_mode').eq('user_id', user.id).maybeSingle()
      .then(({ data }) => setDebugMode(data?.debug_mode ?? false));
  }, [user]);
  return null;
}

function ProtectedApp() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <div className="flex items-center gap-3 text-slate-500 font-mono text-sm">
          <div className="w-5 h-5 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
          Initializing Aurelian Marketing Engine (AME)...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Router>
        <PublicSEO />
        <ReferralTracker />
        <OAuthRedirect isAuthenticated={false} />
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Showcase />} />
            <Route path="/splash/engine" element={<SplashEngine />} />
            <Route path="/splash/automation" element={<SplashAutomation />} />
            <Route path="/splash/affiliate" element={<SplashAffiliate />} />
            <Route path="/splash/ai-marketing" element={<SplashAIMarketing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/login" element={<Login />} />
            <Route path="/legal" element={<LegalHub />} />
            <Route path="/legal/:tab" element={<LegalHub />} />
            <Route path="/terms" element={<LegalHub />} />
            <Route path="/privacy" element={<LegalHub />} />
            <Route path="/cookies" element={<LegalHub />} />
            <Route path="/licensing" element={<LegalHub />} />
            <Route path="/compliance" element={<LegalHub />} />
            <Route path="/showcase/:slug" element={<OutputShowcase />} />
            <Route path="/blog" element={<PublicBlog />} />
            <Route path="/blog/:slug" element={<PublicBlog />} />
            <Route path="/s/:deckKey" element={<PublicSlideshow />} />
            <Route path="/l/:pageKey" element={<PublicLeadPage />} />
            <Route path="/unsubscribed" element={<Unsubscribed />} />
            <Route path="/lead/:pageKey" element={<LeadPageRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  return (
    <TenantProvider>
      <Router>
        <DebugModeLoader />
        <OAuthRedirect isAuthenticated={true} />
        <ReferralTracker />
        <ShellUnlessLeadPage>
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Showcase />} />
              <Route path="/splash/engine" element={<SplashEngine />} />
              <Route path="/splash/automation" element={<SplashAutomation />} />
              <Route path="/splash/affiliate" element={<SplashAffiliate />} />
              <Route path="/splash/ai-marketing" element={<SplashAIMarketing />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/setup" element={<Setup />} />
              <Route path="/research" element={<Research />} />
              <Route path="/generate" element={<ContentGeneration />} />
              <Route path="/library" element={<FeatureRoute feature="content_library"><ContentLibrary /></FeatureRoute>} />
              <Route path="/automation" element={<FeatureRoute feature="automation_pipeline"><Automation /></FeatureRoute>} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/prompt-creator" element={<FeatureRoute feature="prompt_creator"><PromptCreator /></FeatureRoute>} />
              <Route path="/publishing" element={<FeatureRoute feature="publishing_hub"><PublishingHub /></FeatureRoute>} />
              <Route path="/images" element={<ImageGenerator />} />
              <Route path="/covers" element={<FeatureRoute feature="ebook_covers"><CoverPageGen /></FeatureRoute>} />
              <Route path="/rss" element={<FeatureRoute feature="rss_import"><RSSCreator /></FeatureRoute>} />
              <Route path="/media-studio" element={<MediaStudio />} />
              <Route path="/tokens" element={<TokenManagement />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/email" element={<FeatureRoute feature="email_marketing"><EmailMarketing /></FeatureRoute>} />
              <Route path="/live-chat" element={<LiveChatDemo />} />
              <Route path="/support" element={<FeatureRoute feature="live_support"><LiveSupportPage /></FeatureRoute>} />
              <Route path="/affiliates" element={<FeatureRoute feature="affiliate_portal"><AffiliateTracker /></FeatureRoute>} />
              <Route path="/slideshow" element={<FeatureRoute feature="slideshow_builder"><SlideshowBuilder /></FeatureRoute>} />
              <Route path="/leads" element={<LeadCaptureBuilder />} />
              <Route path="/campaigns" element={<FeatureRoute feature="campaigns"><Campaigns /></FeatureRoute>} />
              <Route path="/lead-generation" element={<FeatureRoute feature="lead_generation"><LeadGeneration /></FeatureRoute>} />
              <Route path="/admin-chat" element={<FeatureRoute feature="admin_inbox"><AdminInbox /></FeatureRoute>} />
              <Route path="/questionnaire" element={<FeatureRoute feature="tester_questionnaire"><TesterQuestionnaire /></FeatureRoute>} />
              <Route path="/content-permission" element={<FeatureRoute feature="content_permission"><ContentPermission /></FeatureRoute>} />
              <Route path="/tester-uploads" element={<FeatureRoute feature="tester_uploads"><TesterUploads /></FeatureRoute>} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/admin-testimonials" element={<FeatureRoute feature="testimonial_admin"><TestimonialAdmin /></FeatureRoute>} />
              <Route path="/admin-members" element={<FeatureRoute feature="admin_members"><MembersAdmin /></FeatureRoute>} />
              <Route path="/blog" element={<PublicBlog />} />
              <Route path="/blog/:slug" element={<PublicBlog />} />
              <Route path="/s/:deckKey" element={<PublicSlideshow />} />
              <Route path="/l/:pageKey" element={<PublicLeadPage />} />
              <Route path="/lead/:pageKey" element={<LeadPageRedirect />} />
              <Route path="/marketing-manager" element={<FeatureRoute feature="marketing_manager"><MarketingManager /></FeatureRoute>} />
              <Route path="/output-showcase" element={<FeatureRoute feature="output_showcase"><OutputShowcase /></FeatureRoute>} />
              <Route path="/showcase/:slug" element={<OutputShowcase />} />
              <Route path="/blog-rotation" element={<FeatureRoute feature="blog_rotation"><BlogRotation /></FeatureRoute>} />
              <Route path="/blog-library" element={<FeatureRoute feature="blog_library"><BlogLibrary /></FeatureRoute>} />
              <Route path="/scheduler" element={<FeatureRoute feature="social_scheduler"><SocialScheduler /></FeatureRoute>} />
              <Route path="/social" element={<FeatureRoute feature="social_scheduler"><SocialScheduler /></FeatureRoute>} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/legal" element={<LegalHub />} />
              <Route path="/legal/:tab" element={<LegalHub />} />
              <Route path="/terms" element={<LegalHub />} />
              <Route path="/privacy" element={<LegalHub />} />
              <Route path="/cookies" element={<LegalHub />} />
              <Route path="/licensing" element={<LegalHub />} />
              <Route path="/compliance" element={<LegalHub />} />
              <Route path="/unsubscribed" element={<Unsubscribed />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </ShellUnlessLeadPage>
        <FeedbackWidget />
      </Router>
    </TenantProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <ProtectedApp />
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
